/**
 * MandateEintragungenTab.tsx - the area «Mandate & Eintragungen» of a customer (Stefan P5 V2.0 Kap. 7.7,
 * Use Case 14): one row per Versicherungsbeziehung (the customer and one insurer) with the Analysemandat
 * and the Brokermandat side by side; a row opens the policies (vererbt, ausgenommen, «Courtageberechtigt
 * ab», «nachträglich zugeordnet»), the declared insurances without policy, the proofs of the Meldung and
 * the Brokermandat row's decisions; the filters (Handlungsbedarf, status, mandate) and the sort; the
 * Nachweis-Dossier (PDF) per insurer or for the whole customer; the Prozessabschluss of the Brokermandat.
 *
 * The api derives every status and word (registrationStatusLabel, policyRegistrationLabel) and is the
 * hard line on the permissions: a control shows only for a caller who holds every key its route
 * enforces (the dossier document.download; the row decisions through MandateRegistrationPanel).
 */

import { Fragment, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Download, FileText, Loader2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import {
  REGISTRATION_EXCEPTION_LABEL,
  REGISTRATION_REPLY_OUTCOME_LABEL,
  REGISTRATION_VIEW_STATUS_FILTER_LABEL,
  REGISTRATION_VIEW_STATUS_FILTER_VALUES,
  TERMINATION_STATUS_LABEL,
  formatDateDe,
  formatIsoDayDe,
  formatZurichDateDe,
  gekuendigtPerLabel,
} from '@brokly/shared';
import type {
  CarrierRow,
  RegistrationDossierExportDto,
  RegistrationViewFilters,
  RegistrationViewMandateKind,
  RegistrationViewSort,
  RegistrationViewStatusFilter,
} from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { apiClient } from '../../lib/api-client';
import { saveBlob } from '../../lib/save-blob';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { MandateRegistrationPanel } from './MandateRegistrationPanel';
import { ProcessCloseCard } from './ProcessCloseCard';
import { MandatAufloesenDialog } from './termination/MandatAufloesenDialog';
import { TerminationLetterRow } from './termination/TerminationLetterRow';
import { useExportDossier, useRegistrationView } from './mandate-registration-view-queries';

/** The keys of the dossier export: its route (document.download) and the api's in-code checks (it reads the customer and files a document). */
export const DOSSIER_EXPORT_KEYS = ['crm.read', 'document.upload', 'document.download'] as const;

/** A timestamp as the Swiss calendar day; '-' for none. */
function day(iso: string | null | undefined): string {
  return iso ? (formatZurichDateDe(iso) ?? '-') : '-';
}

function DossierDownload({ result }: { result: RegistrationDossierExportDto }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      data-testid={`dossier-download-${result.document_id}`}
      onClick={async () => {
        setBusy(true);
        try {
          saveBlob(await apiClient.documents.download(result.document_id), result.filename);
        } finally {
          setBusy(false);
        }
      }}
    >
      <Download className="size-3.5" /> Herunterladen
    </Button>
  );
}

function CarrierDetail({
  row,
  brokerMandateId,
  subjectType,
  subjectId,
  onOpenKommunikation,
  terminated,
  readOnly,
}: {
  row: CarrierRow;
  brokerMandateId: string | null;
  /** P6: the Mandatsauflösung is wirksam, no new Meldung. */
  terminated: boolean;
  /** P6: the customer is inaktiv (write-protected): the row's decisions are not offered. */
  readOnly: boolean;
  subjectType: 'person' | 'firma';
  subjectId: string;
  onOpenKommunikation: () => void;
}) {
  const navigate = useNavigate();
  const p = row.proofs;
  const docLink = (id: string | null, label: string) =>
    id ? (
      <button type="button" className="text-primary underline" onClick={() => void navigate({ to: '/ablage/$id', params: { id } })} data-testid={`proof-doc-${id}`}>
        {label}
      </button>
    ) : null;
  const commLink = (id: string | null, label: string) =>
    id ? (
      <button type="button" className="text-primary underline" onClick={onOpenKommunikation} data-testid={`proof-comm-${id}`}>
        {label}
      </button>
    ) : null;

  return (
    <div className="flex flex-col gap-3 px-3 py-2 text-sm" data-testid={`reg-detail-${row.carrier_key}`}>
      {row.carrier_uid_missing && (
        <p className="text-xs text-sem-decide" data-testid="me-missing-uid-hint">
          Diese Policen nennen keinen Versicherer: ohne Versicherer-UID geht keine Meldung. Die Versicherer-UID in der Police erfassen.
        </p>
      )}
      <div>
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">Policen</h4>
        {row.policies.length + row.declared_only.length === 0 ? (
          <p className="text-xs text-muted-foreground">Keine Policen bei diesem Versicherer.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {row.policies.map((pol) => (
              <li key={pol.policy_id} className="flex flex-wrap items-center gap-2" data-testid={`reg-policy-${pol.policy_id}`}>
                <button
                  type="button"
                  className="mono-num text-xs font-medium text-primary underline"
                  onClick={() => void navigate({ to: '/policen/$id', params: { id: pol.policy_id } })}
                  data-testid={`me-policy-link-${pol.policy_id}`}
                >
                  {pol.policy_number ?? 'ohne Nummer'}
                </button>
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{pol.registration_label}</span>
                {pol.inherited_from_policy_number && <span className="text-xs text-muted-foreground">vererbt von {pol.inherited_from_policy_number}</span>}
                {!pol.in_mailed_list && pol.registration_status !== 'nicht_registriert' && (
                  <span className="text-xs text-muted-foreground">nachträglich zugeordnet</span>
                )}
                {pol.courtage_eligible_from && (
                  <span className="text-xs">Courtageberechtigt ab {formatDateDe(pol.courtage_eligible_from)}</span>
                )}
                {/* the window's end (a termination's Enddatum): its own line, readable on its own */}
                {pol.courtage_eligible_to && (
                  <span className="text-xs" data-testid={`me-courtage-to-${pol.policy_id}`}>
                    Courtageberechtigt bis {formatDateDe(pol.courtage_eligible_to)}
                  </span>
                )}
              </li>
            ))}
            {row.declared_only.map((d) => (
              <li key={d.relationship_id} className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid={`reg-declared-${d.relationship_id}`}>
                <span>{d.sparte_text ?? 'Versicherung'}</span>
                <span>{d.policy_number ? `Police Nr. ${d.policy_number}` : 'ohne Policennummer'}</span>
                <span>({d.source_label})</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {p !== null && (
        <div data-testid={`reg-proofs-${row.carrier_key}`}>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">Nachweise</h4>
          <ul className="flex flex-col gap-1 text-xs">
            <li>
              Meldung: {day(p.meldung.sent_at)}
              {p.meldung.recipient_email_masked ? ` an ${p.meldung.recipient_email_masked}` : ''}
              {p.meldung.attachment_checksum ? ` · Anhang-Hash ${p.meldung.attachment_checksum.slice(0, 12)}…` : ''} {commLink(p.meldung.communication_id, 'E-Mail')}{' '}
              {docLink(p.meldung.document_id, 'Mandat')}
            </li>
            <li>
              Zustellnachweis:{' '}
              {p.zustellnachweis ? `Versand bestätigt durch den Mailserver am ${day(p.meldung.sent_at)}` : 'keiner'}
            </li>
            <li>
              Erinnerung: {p.erinnerung ? day(p.erinnerung.at) : 'keine'} {p.erinnerung ? commLink(p.erinnerung.communication_id, 'E-Mail') : null}
            </li>
            <li>
              Bestätigung: {p.bestaetigung ? day(p.bestaetigung.at) : 'keine'}
              {p.bestaetigung ? ' ' : ''}
              {p.bestaetigung ? commLink(p.bestaetigung.communication_id, 'E-Mail') : null} {p.bestaetigung ? docLink(p.bestaetigung.document_id, 'Dokument') : null}
            </li>
            {p.replies.map((r) => (
              <li key={r.id} className="text-muted-foreground">
                Rückmeldung {day(r.at)}: {REGISTRATION_REPLY_OUTCOME_LABEL[r.outcome as keyof typeof REGISTRATION_REPLY_OUTCOME_LABEL] ?? r.outcome}
              </li>
            ))}
          </ul>
        </div>
      )}

      {row.aufloesung && (
        <div data-testid={`me-aufloesung-${row.aufloesung.registration_id}`}>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">Auflösung</h4>
          <p className="text-xs">
            {row.aufloesung.status_label}
            {row.aufloesung.sent_at ? ` · gemeldet ${day(row.aufloesung.sent_at)}` : ''}
            {row.aufloesung.response_due_at && !row.aufloesung.deregistered_at ? ` · Frist ${day(row.aufloesung.response_due_at)}` : ''}
          </p>
          {/* after a Widerruf: the Rücknahmemeldung to this insurer */}
          {row.aufloesung.withdrawal_sent_at ? (
            <p className="text-xs" data-testid={`me-ruecknahme-${row.aufloesung.registration_id}`}>
              Rücknahmemeldung versandt am {day(row.aufloesung.withdrawal_sent_at)}
            </p>
          ) : row.aufloesung.withdrawal_pending ? (
            <p className="text-xs text-sem-decide" data-testid={`me-ruecknahme-${row.aufloesung.registration_id}`}>
              Rücknahmemeldung manuell senden
            </p>
          ) : null}
          {/* the Auflösungsmeldung's own actions (Freigeben, Jetzt melden, Austragung bestätigen, verweigert, Schliessen, Erinnern) */}
          <MandateRegistrationPanel
            mandateId={row.aufloesung.mandate_id}
            subjectType={subjectType}
            subjectId={subjectId}
            onlyRegistrationId={row.aufloesung.registration_id}
          />
        </div>
      )}

      {row.broker !== null && brokerMandateId !== null && row.broker.mandate_id === brokerMandateId && (
        <MandateRegistrationPanel mandateId={brokerMandateId} subjectType={subjectType} subjectId={subjectId} onlyRegistrationId={row.broker.registration_id} terminated={terminated} readOnly={readOnly} />
      )}
    </div>
  );
}

export function MandateEintragungenTab({
  subjectType,
  subjectId,
  onOpenKommunikation,
}: {
  subjectType: 'person' | 'firma';
  subjectId: string;
  onOpenKommunikation: () => void;
}) {
  const [handlungsbedarf, setHandlungsbedarf] = useState(false);
  const [status, setStatus] = useState<RegistrationViewStatusFilter | ''>('');
  const [mandateKind, setMandateKind] = useState<RegistrationViewMandateKind | ''>('');
  const [sort, setSort] = useState<RegistrationViewSort>('carrier');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [exports, setExports] = useState<Record<string, RegistrationDossierExportDto>>({});
  const [terminationOpen, setTerminationOpen] = useState(false);
  const filters: RegistrationViewFilters = {
    ...(handlungsbedarf ? { handlungsbedarf: true } : {}),
    ...(status !== '' ? { status } : {}),
    ...(mandateKind !== '' ? { mandate_kind: mandateKind } : {}),
    ...(sort !== 'carrier' ? { sort } : {}),
  };
  const view = useRegistrationView(subjectType, subjectId, filters);
  const exportDossier = useExportDossier(subjectType, subjectId);
  const { can } = useEffectivePermissions();
  const canExport = DOSSIER_EXPORT_KEYS.every((k) => can(k));

  function toggle(key: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function doExport(key: string | null) {
    exportDossier.mutate(key !== null ? { carrier_key: key } : {}, {
      onSuccess: (res) => setExports((prev) => ({ ...prev, [key ?? '*']: res })),
    });
  }

  if (view.isError) {
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" /> Die Mandate und Eintragungen konnten nicht geladen werden.
      </p>
    );
  }
  const data = view.data;
  if (data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt …
      </p>
    );
  }
  const brokerMandate = data.mandates.broker;
  const anyBroker = data.tiles.total > 0;
  // P6: the Mandatsauflösung of the customer (the live one, else the last completed one).
  const termination = data.termination ?? null;
  const subjectStatus = String(data.subject.status);
  const terminable = subjectStatus === 'aktiv' || subjectStatus === 'provisorisch';
  // The export files a document on the customer: refused (409) once the customer is inaktiv.
  const canExportHere = canExport && subjectStatus !== 'inaktiv';
  const canTerminate = can('mandate.eingangskontrolle');
  const canRevoke = can('customer_status.override');

  return (
    <section className="flex flex-col gap-4" aria-label="Mandate & Eintragungen" data-testid="mandate-eintragungen">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col">
          <h3 className="text-sm font-semibold">Eintragung bei Versicherern</h3>
          <p className="text-xs text-muted-foreground" data-testid="me-tile-label">
            {data.tiles.label}
            {brokerMandate?.process_number ? ` · ${brokerMandate.process_number}` : ''}
            {data.mandates.analyse?.process_number ? ` · ${data.mandates.analyse.process_number}` : ''}
          </p>
        </div>
        {data.austragung && (
          <div className="flex flex-col" data-testid="me-austragung-tile">
            <h3 className="text-sm font-semibold">Austragung bei Versicherern</h3>
            <p className="text-xs text-muted-foreground">
              {(
                [
                  ['gemeldet', 'gemeldet'],
                  ['erinnert', 'erinnert'],
                  ['klaerung', 'Klärung'],
                  ['ausgetragen', 'ausgetragen'],
                  ['informiert', 'informiert'],
                ] as const
              )
                .filter(([k]) => data.austragung![k] > 0)
                .map(([k, word]) => `${data.austragung![k]} ${word}`)
                .join(' · ') || 'keine offenen Meldungen'}
            </p>
          </div>
        )}
        {canExportHere && anyBroker && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={exportDossier.isPending} onClick={() => doExport(null)} data-testid="dossier-export-all">
              <FileText className="size-3.5" /> Nachweis-Dossier (PDF)
            </Button>
            {exports['*'] && <DossierDownload result={exports['*']} />}
          </div>
        )}
      </div>

      {(termination !== null || (canTerminate && terminable)) && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-sm" data-testid="me-termination">
          {termination !== null ? (
            <span>
              Mandatsauflösung · {TERMINATION_STATUS_LABEL[termination.status]}
              {termination.status === 'wirksam' || termination.status === 'abgeschlossen'
                ? ` · ${gekuendigtPerLabel(termination.end_date)}`
                : termination.status === 'widerrufen'
                  ? // P6-S3: no Enddatum for a revoked one (the customer stays), shown 30 days; the Zurich day
                    termination.revoked_at
                    ? ` am ${formatZurichDateDe(termination.revoked_at) ?? '-'}`
                    : ''
                  : termination.end_date
                  ? ` · Enddatum ${formatIsoDayDe(termination.end_date)}`
                  : ''}
            </span>
          ) : (
            <span className="text-muted-foreground">Keine Mandatsauflösung</span>
          )}
          {subjectStatus !== 'inaktiv' && (
            // The Widerruf works like an override (customer_status.override).
            termination?.status === 'wirksam' ? (
              canRevoke && <Button size="sm" variant="outline" onClick={() => setTerminationOpen(true)}>
                Kündigung widerrufen
              </Button>
            ) : canTerminate && terminable && (termination === null || termination.status === 'eroeffnet' || termination.status === 'in_pruefung' || termination.status === 'widerrufen') ? (
              <Button size="sm" variant="outline" onClick={() => setTerminationOpen(true)}>
                Mandat auflösen
              </Button>
            ) : null
          )}
          {/* P6-S3: the Dankesbrief exists from the Freigabe on */}
          {termination !== null && ['wirksam', 'widerrufen', 'abgeschlossen'].includes(termination.status) && (
            <TerminationLetterRow terminationId={termination.id} readOnly={subjectStatus === 'inaktiv'} />
          )}
        </div>
      )}
      {terminationOpen && (
        <MandatAufloesenDialog
          subjectType={subjectType}
          subjectId={subjectId}
          onClose={() => setTerminationOpen(false)}
          onDone={() => setTerminationOpen(false)}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={handlungsbedarf} onChange={(e) => setHandlungsbedarf(e.target.checked)} data-testid="me-filter-handlungsbedarf" />
          Handlungsbedarf
        </label>
        <select className="rounded border px-2 py-1" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as RegistrationViewStatusFilter | '')}>
          <option value="">Alle Status</option>
          {REGISTRATION_VIEW_STATUS_FILTER_VALUES.map((v) => (
            <option key={v} value={v}>
              {REGISTRATION_VIEW_STATUS_FILTER_LABEL[v]}
            </option>
          ))}
        </select>
        <select className="rounded border px-2 py-1" aria-label="Mandat" value={mandateKind} onChange={(e) => setMandateKind(e.target.value as RegistrationViewMandateKind | '')}>
          <option value="">Beide Mandate</option>
          <option value="analyse">Analysemandat</option>
          <option value="broker">Brokermandat</option>
        </select>
        <select className="rounded border px-2 py-1" aria-label="Sortierung" value={sort} onChange={(e) => setSort(e.target.value as RegistrationViewSort)}>
          <option value="carrier">nach Versicherer</option>
          <option value="status">nach Status</option>
        </select>
      </div>

      {exportDossier.isError && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert" data-testid="dossier-error">
          <AlertTriangle className="size-4" /> Das Nachweis-Dossier konnte nicht erstellt werden.
        </p>
      )}

      {data.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="me-empty">
          Keine Versicherungsbeziehung{handlungsbedarf || status !== '' || mandateKind !== '' ? ' für diesen Filter' : ''}.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="w-6" />
              <th className="py-1">Versicherer</th>
              <th className="py-1">Aktive Policen</th>
              <th className="py-1">Analysemandat</th>
              <th className="py-1">Brokermandat</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const expanded = open.has(r.carrier_key);
              return (
                <Fragment key={r.carrier_key}>
                  <tr className="border-t" data-testid={`me-row-${r.carrier_key}`}>
                    <td>
                      <button type="button" aria-label={expanded ? 'Zuklappen' : 'Aufklappen'} aria-expanded={expanded} onClick={() => toggle(r.carrier_key)} data-testid={`me-expand-${r.carrier_key}`}>
                        {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                      </button>
                    </td>
                    <td className="py-1 font-medium">{r.carrier_uid_missing ? 'Ohne Versicherer-UID' : (r.carrier_name ?? r.carrier_uid ?? r.carrier_key)}</td>
                    <td className="py-1 mono-num">{r.active_policy_count}</td>
                    <td className="py-1" data-testid="me-analyse">
                      {r.analyse ? r.analyse.status_label : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="py-1" data-testid="me-broker">
                      {r.broker ? (
                        <span className="flex flex-wrap items-center gap-1">
                          <span>{r.broker.status_label}</span>
                          {r.broker.exception_codes
                            .filter((c) => c !== 'send_pending')
                            .map((c) => (
                              <span key={c} className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide">
                                {REGISTRATION_EXCEPTION_LABEL[c] ?? c}
                              </span>
                            ))}
                          {r.broker.overdue && <span className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide">überfällig</span>}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{r.carrier_uid_missing ? 'Versicherer-UID fehlt' : 'nicht gemeldet'}</span>
                      )}
                    </td>
                    <td className="py-1 text-right">
                      {canExportHere && r.broker !== null && (
                        <span className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" disabled={exportDossier.isPending} onClick={() => doExport(r.carrier_key)} data-testid={`dossier-export-${r.carrier_key}`}>
                            Nachweis-Dossier (PDF)
                          </Button>
                          {exports[r.carrier_key] && <DossierDownload result={exports[r.carrier_key]!} />}
                        </span>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={6}>
                        <CarrierDetail
                          row={r}
                          brokerMandateId={brokerMandate?.mandate_id ?? null}
                          subjectType={subjectType}
                          subjectId={subjectId}
                          onOpenKommunikation={onOpenKommunikation}
                          terminated={termination?.status === 'wirksam'}
                          readOnly={subjectStatus === 'inaktiv'}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {brokerMandate !== null && brokerMandate.status === 'signed' && (
        <ProcessCloseCard mandateId={brokerMandate.mandate_id} kind="broker" subject={{ subjectType, subjectId }} />
      )}
    </section>
  );
}
